/* Single top-hinged front panel on an outdoor kitchen pod.
   Units: inches, lbf, degrees. Hinge-relative +X is outward, +Y is upward.
   Opening angle is measured from the vertically hanging closed panel:
   0 degrees closed, 90 degrees a horizontal canopy. Positive actuator force
   is push; negative is pull. The two sides share load equally. The panel's
   centre of gravity is at half its height. Optional model resistance is total
   torque; the BBQ Pod calculator uses zero, with no resistance input.
   Forces are raw quasi-static demands, with no hidden allowance or efficiency.
   Wind, snow, brackets, panel flexure, synchronization and collision clearance
   require separate checks. Pin-centre geometry does not establish those checks. */
(function (root) {
  'use strict';
  const VERSION = '1.0.0';
  const R = Math.PI / 180, EPS = 1e-8, MIN_LEVER_ANGLE = 2;
  const DEFAULTS = Object.freeze({
    podHeight: 84, podWidth: 120, podDepth: 24,
    panelWeight: 220, openAngle: 90,
    baseX: -4, baseY: -66, mountAlong: 24, mountOffset: 0,
    actuatorCount: 2, resistanceTorque: 0
  });
  const finite = value => typeof value === 'number' && Number.isFinite(value);
  const requireValue = (condition, message) => {if (!condition) throw new Error(message);};
  const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  function parameters(input, allowNewBase = false) {
    requireValue(input == null || (typeof input === 'object' && !Array.isArray(input)), 'Enter the pod dimensions and mounting positions.');
    const p = {...DEFAULTS, ...input};
    for (const key of Object.keys(DEFAULTS)) requireValue(finite(p[key]), 'Enter a finite number for ' + key + '.');
    requireValue(p.podHeight > 0 && p.podWidth > 0 && p.podDepth > 0, 'Pod height, width and depth must be positive.');
    requireValue(p.panelWeight >= 0 && p.resistanceTorque >= 0, 'Panel weight and hinge resistance cannot be negative.');
    requireValue(p.openAngle > 0 && p.openAngle <= 110, 'Choose an opening angle greater than 0° and no greater than 110° from the closed vertical panel.');
    requireValue(p.mountAlong > 0 && p.mountAlong <= p.podHeight, 'Place the moving pin between the hinge and the bottom edge of the panel.');
    requireValue(Math.abs(p.mountOffset) <= p.podDepth, 'Keep the moving-pin perpendicular bracket offset no larger than the pod depth.');
    if (!allowNewBase) requireValue(p.baseX >= -p.podDepth && p.baseX <= 0 && p.baseY >= -p.podHeight && p.baseY <= 0,
      'Place the fixed pin inside the pod side: X from minus its depth to 0, Y from minus its height to 0.');
    requireValue(Number.isInteger(p.actuatorCount) && p.actuatorCount >= 1 && p.actuatorCount <= 4, 'Use a whole number of actuators between 1 and 4.');
    requireValue(Number.isFinite(p.panelWeight * (p.podHeight / 2)), 'The entered dimensions or weight are too large to calculate.');
    return p;
  }
  function localPin(p, theta) {
    const s = Math.sin(theta), c = Math.cos(theta);
    return [p.mountAlong * s + p.mountOffset * c, -p.mountAlong * c + p.mountOffset * s];
  }
  function pinData(p, theta) {
    const pin = localPin(p, theta), dx = pin[0] - p.baseX, dy = pin[1] - p.baseY;
    const length = Math.hypot(dx, dy);
    requireValue(finite(length) && length > EPS, 'The actuator pins coincide or the mounting dimensions are too large. Move a mounting pin.');
    // r cross force-unit-vector = d(pin length)/d(opening angle).
    const momentArm = pin[0] * (dy / length) - pin[1] * (dx / length);
    return {pin, length, momentArm};
  }
  function prepare(input) {
    const p = parameters(input), lo = 0, hi = p.openAngle * R;
    const pinRadius = Math.hypot(p.mountAlong, p.mountOffset), baseRadius = Math.hypot(p.baseX, p.baseY);
    requireValue(finite(pinRadius) && finite(baseRadius) && baseRadius > EPS, 'The fixed actuator pin cannot be at the panel hinge.');
    // Check exact collinearity events in the complete interval, including its
    // endpoints. A sparse playback sample must never miss an interior reversal.
    const rootAngle = Math.atan2(p.baseY, p.baseX) - Math.atan2(-p.mountAlong, p.mountOffset);
    const first = Math.ceil((lo - rootAngle - 1e-10) / Math.PI);
    const last = Math.floor((hi - rootAngle + 1e-10) / Math.PI);
    requireValue(first > last, 'The actuator reaches a dead center in this opening range. Move its mounting pins or reduce the opening angle.');
    let minLeverageRatio = Infinity;
    // With no collinearity crossing, |moment arm| has only an interior maximum;
    // its minimum over this interval occurs at one of the two endpoints.
    for (const theta of [lo, hi]) {
      const point = pinData(p, theta), ratio = Math.abs(point.momentArm) / pinRadius;
      requireValue(finite(ratio) && ratio >= Math.sin(MIN_LEVER_ANGLE * R),
        'The actuator is within 2° of a dead-center alignment. Move its mounting pins for usable leverage.');
      minLeverageRatio = Math.min(minLeverageRatio, ratio);
    }
    return {p, lo, hi, pinRadius, minLeverageRatio, gravityScale: p.panelWeight * (p.podHeight / 2)};
  }
  function at(prepared, t) {
    requireValue(finite(t) && t >= 0 && t <= 1, 'Animation position must be between 0 and 1.');
    const {p, hi, gravityScale} = prepared, theta = hi * t;
    const data = pinData(p, theta), s = Math.sin(theta), c = Math.cos(theta);
    const hinge = [0, p.podHeight], base = [p.baseX, p.podHeight + p.baseY];
    const pin = [data.pin[0], p.podHeight + data.pin[1]];
    const panelTip = [p.podHeight * s, p.podHeight - p.podHeight * c];
    const panelCG = [p.podHeight / 2 * s, p.podHeight - p.podHeight / 2 * c];
    const gravityTorque = gravityScale * s;
    const signedOpenForce = (gravityTorque + p.resistanceTorque) / data.momentArm / p.actuatorCount;
    const signedCloseForce = (gravityTorque - p.resistanceTorque) / data.momentArm / p.actuatorCount;
    requireValue([signedOpenForce, signedCloseForce, ...pin, ...panelTip].every(Number.isFinite), 'The entered dimensions or loads are too large to calculate.');
    const pushDemand = Math.max(0, signedOpenForce, signedCloseForce), pullDemand = Math.max(0, -signedOpenForce, -signedCloseForce);
    return {t, angle: theta / R, theta, hinge, base, pin, localPin: data.pin,
      panelTip, panelCG, panelDirection: [s, -c], panelNormal: [c, s],
      length: data.length, momentArm: data.momentArm, gravityTorque,
      signedOpenForce, signedCloseForce, rawOpenForce: signedOpenForce, rawCloseForce: signedCloseForce,
      pushDemand, pullDemand, force: Math.max(pushDemand, pullDemand)};
  }
  function geometry(input = DEFAULTS, t = 0) {return at(prepare(input), t);}
  function preview(input = DEFAULTS, t = 0) {
    // A design preview may show an unusable linkage so its bracket positions
    // remain editable. It never computes or certifies actuator force or fit.
    // Keep solve/geometry on the separate guarded path above.
    const p = parameters(input);
    requireValue(finite(t) && t >= 0 && t <= 1, 'Animation position must be between 0 and 1.');
    const theta = p.openAngle * R * t, s = Math.sin(theta), c = Math.cos(theta);
    const hinge = [0, p.podHeight], base = [p.baseX, p.podHeight + p.baseY];
    const local = localPin(p, theta), pin = [local[0], p.podHeight + local[1]];
    const panelTip = [p.podHeight * s, p.podHeight - p.podHeight * c];
    const panelCG = [p.podHeight / 2 * s, p.podHeight - p.podHeight / 2 * c];
    const length = Math.hypot(local[0] - p.baseX, local[1] - p.baseY);
    requireValue([theta, ...hinge, ...base, ...local, ...pin, ...panelTip, ...panelCG, length].every(finite),
      'The entered dimensions are too large to preview.');
    return {t, angle: theta / R, theta, hinge, base, pin, localPin: local,
      panelTip, panelCG, panelDirection: [s, -c], panelNormal: [c, s], length,
      momentArm: null, gravityTorque: null,
      signedOpenForce: null, signedCloseForce: null, rawOpenForce: null, rawCloseForce: null,
      pushDemand: null, pullDemand: null, force: null, previewOnly: true};
  }
  function refine(prepared, samples, key) {
    let best = samples.reduce((a, b) => b[key] > a[key] ? b : a);
    for (let i = 1; i < samples.length - 1; i++) {
      if (samples[i][key] < samples[i - 1][key] || samples[i][key] < samples[i + 1][key]) continue;
      if (samples[i][key] === samples[i - 1][key] && samples[i][key] === samples[i + 1][key]) continue;
      let a = samples[i - 1].t, b = samples[i + 1].t;
      for (let k = 0; k < 32; k++) {
        const u = a + (b - a) / 3, v = b - (b - a) / 3;
        if (at(prepared, u)[key] < at(prepared, v)[key]) a = u; else b = v;
      }
      const point = at(prepared, (a + b) / 2);
      if (point[key] > best[key]) best = point;
    }
    return best;
  }
  function solved(prepared) {
    const samples = Array.from({length: 241}, (_, i) => at(prepared, i / 240));
    const closed = samples[0], open = samples[samples.length - 1];
    // The continuous dead-center check establishes monotonic pin length.
    const minLength = Math.min(closed.length, open.length), maxLength = Math.max(closed.length, open.length);
    const push = refine(prepared, samples, 'pushDemand'), pull = refine(prepared, samples, 'pullDemand');
    const peakSample = push.pushDemand >= pull.pullDemand ? push : pull;
    return {version: VERSION, parameters: {...prepared.p}, samples, closed, open,
      minLength, maxLength, stroke: maxLength - minLength,
      peak: Math.max(push.pushDemand, pull.pullDemand), peakAt: peakSample.t,
      maxPush: push.pushDemand, maxPull: pull.pullDemand, pushPeakAt: push.t, pullPeakAt: pull.t,
      openingExtends: open.length > closed.length, actuatorCount: prepared.p.actuatorCount,
      totalMovingWeight: prepared.p.panelWeight, minLeverageRatio: prepared.minLeverageRatio};
  }
  function solve(input = DEFAULTS) {return solved(prepare(input));}
  function fitModel(input, closedLength, usableStroke, options = {}) {
    // Explicit proposal only. No source state is modified, no moving pin or
    // pod dimension changes, and no bracket outside the pod side is invented.
    // Current dead-center or out-of-side fixed pins may be repaired by a fit.
    const p = parameters(input, true);
    requireValue(finite(closedLength) && closedLength > 0 && finite(usableStroke) && usableStroke > 0 && finite(closedLength + usableStroke),
      'Actuator fitting needs a positive retracted length and usable stroke.');
    const positionStep = options.positionStep;
    requireValue(positionStep === undefined || (finite(positionStep) && positionStep > 0), 'Mounting position step must be a positive finite number.');
    const forceLimit = options.forceLimit ?? Infinity;
    const pushLimit = options.pushLimit ?? forceLimit, pullLimit = options.pullLimit ?? forceLimit;
    for (const limit of [forceLimit, pushLimit, pullLimit]) requireValue((finite(limit) || limit === Infinity) && limit >= 0, 'Force limits must be nonnegative.');
    const maxLength = closedLength + usableStroke;
    let best = null, checked = 0;
    const visited = new Set();
    function tryBase(x, y) {
      // Search and validate the same positions that the UI can display and
      // reproduce. Never validate a precise position and round it afterward.
      if (positionStep !== undefined) {
        x = Math.round(x / positionStep) * positionStep;
        y = Math.round(y / positionStep) * positionStep;
      }
      if (x < -p.podDepth - EPS || x > EPS || y < -p.podHeight - EPS || y > EPS) return;
      // Clamping a grid position to an arbitrary frame boundary would move it
      // off-grid. Non-grid searches retain the original boundary tolerance.
      if (positionStep === undefined) {
        x = Math.min(0, Math.max(-p.podDepth, x)); y = Math.min(0, Math.max(-p.podHeight, y));
      }
      const key = positionStep === undefined ? x.toFixed(6) + ':' + y.toFixed(6) : Math.round(x / positionStep) + ':' + Math.round(y / positionStep);
      if (visited.has(key)) return;
      visited.add(key); checked++;
      const distanceMoved = Math.hypot(x - p.baseX, y - p.baseY);
      if (best && distanceMoved >= best.distance - EPS) return;
      try {
        const candidate = prepare({...p, baseX: x, baseY: y}), start = at(candidate, 0), end = at(candidate, 1);
        const min = Math.min(start.length, end.length), max = Math.max(start.length, end.length);
        if (min < closedLength - 1e-7 || max > maxLength + 1e-7) return;
        const result = solved(candidate);
        if (result.peak > forceLimit + 1e-7 || result.maxPush > pushLimit + 1e-7 || result.maxPull > pullLimit + 1e-7) return;
        best = {parameters: {...candidate.p}, baseX: x, baseY: y, result, distance: distanceMoved, moved: distanceMoved > EPS};
      } catch (_) { /* Rejected geometry is not a mounting proposal. */ }
    }
    tryBase(p.baseX, p.baseY);
    if (best) return {...best, checked};
    for (let i = 0; i <= 30; i++) for (let j = 0; j <= 30; j++) tryBase(-p.podDepth * i / 30, -p.podHeight * j / 30);
    // Endpoint circles supplement the grid for narrow feasible mounting areas.
    for (const theta of [0, p.openAngle * R]) {
      const pin = localPin(p, theta);
      for (const radius of [closedLength, closedLength + usableStroke / 2, maxLength]) {
        for (let k = 0; k < 120; k++) {
          const a = k * Math.PI / 60;
          const x = pin[0] + radius * Math.cos(a), y = pin[1] + radius * Math.sin(a);
          tryBase(x, y);
          // A rounded point on a length-limit circle may land on its rejected
          // side. Check its adjacent grid points for the feasible side too.
          if (positionStep !== undefined) for (const dx of [-1, 0, 1]) for (const dy of [-1, 0, 1]) {
            if (dx || dy) tryBase(x + dx * positionStep, y + dy * positionStep);
          }
        }
      }
    }
    if (!best) return null;
    let stepX = p.podDepth / 30, stepY = p.podHeight / 30;
    for (let pass = 0; pass < 8; pass++) {
      const b = best;
      for (const dx of [-1, 0, 1]) for (const dy of [-1, 0, 1]) tryBase(b.baseX + dx * stepX, b.baseY + dy * stepY);
      stepX /= 2; stepY /= 2;
    }
    return {...best, checked};
  }
  const api = {version: VERSION, DEFAULTS, defaults: DEFAULTS, geometry, preview, solve, fitModel};
  root.FGOutdoorKitchenPodModel = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
